import { money } from '@/lib/format'
import type { CurrencyTrend } from '@/lib/payoutTrend'

/**
 * A month of payouts, drawn.
 *
 * Hand-drawn SVG rather than a charting library. The staff application's Analytics screen
 * draws its figures the same way, this portal has one chart in it, and a dependency here
 * would be shipped to every partner on every load to draw twelve rectangles.
 *
 * **One chart per currency.** The caller renders one of these for each; nothing in here
 * can combine two, because there is no rate to combine them at.
 */

const WIDTH = 720
const AXIS = 54
const RIGHT = 10
const TOP = 14
const PLOT = 132
const LABELS = 22

/** Compact axis text - `40k`, `1.2m` - because the exact figure is the bar's own label. */
function tick(value: number): string {
  const abs = Math.abs(value)
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}m`
  if (abs >= 1_000) return `${(value / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}k`

  return String(Math.round(value))
}

/**
 * A round number at or above the largest bar, so the axis reads 40k rather than 38.4k.
 * Zero stays zero: a currency with only reversals has no positive axis to round up to.
 */
function ceilNice(value: number): number {
  if (value <= 0) return 0

  const magnitude = 10 ** Math.floor(Math.log10(value))
  const steps = [1, 1.5, 2, 2.5, 3, 4, 5, 7.5, 10]

  for (const step of steps) {
    if (value <= step * magnitude) return step * magnitude
  }

  return 10 * magnitude
}

export function PayoutChart({ trend }: { trend: CurrencyTrend }) {
  const amounts = trend.months.map((month) => month.amount)
  const high = ceilNice(Math.max(0, ...amounts))
  const low = -ceilNice(Math.abs(Math.min(0, ...amounts)))

  // A currency whose every month is nil still gets a baseline and twelve labels rather
  // than a division by zero - it is a true answer, and an axis is how it reads as one.
  const span = high - low || 1
  const zeroY = TOP + ((high / span) * PLOT)
  const bottom = TOP + PLOT

  const slot = (WIDTH - AXIS - RIGHT) / trend.months.length
  const barWidth = Math.min(38, slot * 0.62)
  const height = bottom + LABELS + 8

  const hasReversal = amounts.some((amount) => amount < 0)

  // Four gridlines across whatever the axis spans, the zero line drawn separately below.
  const gridlines = [0.25, 0.5, 0.75].map((fraction) => TOP + fraction * PLOT)

  return (
    <div>
      <svg
        viewBox={`0 0 ${WIDTH} ${height}`}
        className="block h-auto w-full"
        role="img"
        aria-label={summarise(trend)}
      >
        {gridlines.map((y) => (
          <line key={y} x1={AXIS} y1={y} x2={WIDTH - RIGHT} y2={y} className="stroke-line-soft" strokeWidth="1" />
        ))}

        {/* The zero line, always drawn and always heavier than a gridline: it is what a
            bar below it is below. */}
        <line x1={AXIS} y1={zeroY} x2={WIDTH - RIGHT} y2={zeroY} className="stroke-line" strokeWidth="1" />

        <g className="fill-faint font-mono text-[10px]" textAnchor="end">
          <text x={AXIS - 8} y={TOP + 4}>{tick(high)}</text>
          <text x={AXIS - 8} y={zeroY + 4}>0</text>
          {low < 0 && <text x={AXIS - 8} y={bottom + 4}>{tick(low)}</text>}
        </g>

        {trend.months.map((month, index) => {
          const centre = AXIS + slot * index + slot / 2
          const length = Math.abs(month.amount) / span * PLOT
          const negative = month.amount < 0

          return (
            <g key={month.key}>
              {month.amount !== 0 && (
                <rect
                  x={centre - barWidth / 2}
                  y={negative ? zeroY : zeroY - length}
                  width={barWidth}
                  height={Math.max(1.5, length)}
                  rx="2"
                  className={negative ? 'fill-crit' : 'fill-accent'}
                >
                  <title>{`${month.label}: ${trend.currencyCode} ${money(month.amount, trend.currencyCode)}`}</title>
                </rect>
              )}

              <text
                x={centre}
                y={bottom + LABELS - 4}
                textAnchor="middle"
                className="fill-muted font-mono text-[10px]"
              >
                {month.label}
              </text>
            </g>
          )
        })}
      </svg>

      {hasReversal && (
        <p className="mt-2 flex items-center gap-1.5 text-[12px] text-muted">
          <span aria-hidden className="size-2 rounded-[2px] bg-crit" />
          A month below the line is net of a reversed payout.
        </p>
      )}
    </div>
  )
}

/**
 * What a screen reader is told instead of the drawing.
 *
 * The window's total and its best month, rather than twelve figures read aloud - the
 * Payouts screen already lists every payout in a table, which is the accessible form of
 * the detail.
 */
function summarise(trend: CurrencyTrend): string {
  const paid = trend.months.filter((month) => month.amount !== 0)

  if (paid.length === 0) {
    return `No ${trend.currencyCode} payouts in the last ${trend.months.length} months.`
  }

  const best = paid.reduce((a, b) => (b.amount > a.amount ? b : a))

  return (
    `Monthly ${trend.currencyCode} payouts over ${trend.months.length} months, ` +
    `totalling ${money(trend.total, trend.currencyCode)} across ${paid.length} ` +
    `${paid.length === 1 ? 'month' : 'months'}. ` +
    `Highest was ${best.label}, ${money(best.amount, trend.currencyCode)}.`
  )
}
